import PageTitle from "../Common/PageTitle"
import ToolBar from "../Common/Toolbar"
import { FC, Fragment, useEffect, useState } from "react"
import { Box, Typography } from "@mui/material"
import { HomeWorkOutlined, ReceiptOutlined, TrendingDownOutlined, TrendingUpOutlined, WarningAmberOutlined } from "@mui/icons-material"
import { Apis, numberWithCommas } from "../../helpers"
import { PropertyProps } from "../../types/properties"
import NoData from "../Common/NoData"
import { useNavigate } from "react-router-dom"
import { Connect, mapStateToProps } from "../../Redux"
import moment from 'moment'
import { TenantProps } from "../../types/tenants"

interface SummaryProps {
    _id: string
    invoiceNo: number
    date: Date
    revenue: number
    expenditures: number
    arrears: number
    vacant: number
    arrearsBF: number
    arrearsCF: number
}
interface StateProps {
    data: SummaryProps[]
    loading: boolean
    refreshing: boolean
}
interface Props {
    tenant?: TenantProps
    selectedProperty?: PropertyProps
}

const stats = [
    { label: 'Revenue', field: 'revenue', money: true, icon: <TrendingUpOutlined sx={{ fontSize: 14 }} />, color: '#6a8c0c' },
    { label: 'Expenditure', field: 'expenditures', money: true, icon: <TrendingDownOutlined sx={{ fontSize: 14 }} />, color: '#c05c13' },
    { label: 'Arrears', field: 'arrears', money: true, icon: <WarningAmberOutlined sx={{ fontSize: 14 }} />, color: '#d4890a' },
    { label: 'Vacant Units', field: 'vacant', money: false, icon: <HomeWorkOutlined sx={{ fontSize: 14 }} />, color: '#8c7c0c' }
]

const Invoices: FC<Props> = ({
    selectedProperty,
    tenant
}) => {
    const [state, setState] = useState<StateProps>({
        data: [],
        loading: true,
        refreshing: false
    })
    const navigate = useNavigate()
    const { data, loading, refreshing } = state

    const getPropertyInvoices = ({
        refreshing
    }: any) => {
        if (refreshing)
            setState({ ...state, refreshing: true })
        Apis({
            module: 'Invoices',
            action: 'getInvoices',
            propertyId: selectedProperty?._id,
            pageSize: 10,
            page: 0
        }).then((resp: any) => {
            setState({ ...state, data: resp.data, loading: false, refreshing: false })
        }).catch(() => {
            setState({ ...state, loading: false, refreshing: false })
        })
    }
    useEffect(() => {
        getPropertyInvoices({})
        // eslint-disable-next-line
    }, [])
    return (
        <Box sx={{ height: '100%', pr: 5 }}>
            {tenant ? <Fragment /> : <PageTitle title={"Summary"} />}
            {!loading && data.length ?
                <ToolBar
                    noAddButton
                    noSearch
                    refreshing={refreshing}
                    onRefresh={() => getPropertyInvoices({ refreshing: true })}
                />
                : null}
            {
                !data?.length || loading ?
                    <NoData
                        loading={loading}
                    /> :
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 3 }}>
                        {data.map((record) => {
                            const net = (record.revenue || 0) - (record.expenditures || 0)
                            const viewInvoice = () => navigate('/invoice', { state: { record } })
                            return (
                                <Box
                                    key={record._id}
                                    onClick={viewInvoice}
                                    sx={{
                                        display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#fff', borderRadius: 3, p: 2,
                                        boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)', cursor: 'pointer',
                                        transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,.1), 0 6px 20px rgba(0,0,0,.06)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 30, height: 30, borderRadius: 2, bgcolor: 'rgba(140,124,12,.1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <ReceiptOutlined sx={{ fontSize: 16, color: '#8c7c0c' }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                                                Summary No. {record.invoiceNo}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                                            {moment(record.date).format('DD MMM YYYY')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                        {stats.map(({ label, field, money, icon, color }) => {
                                            const value = (record as any)[field] || 0
                                            return (
                                                <Box key={label} sx={{
                                                    flex: '1 1 140px', minWidth: 120, bgcolor: '#f8fafc', borderRadius: 2, p: 1.2
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
                                                        <Box sx={{ color }}>{icon}</Box>
                                                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                                                            {label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                                                        {money ? `Ksh ${numberWithCommas(value)}` : value}
                                                    </Typography>
                                                </Box>
                                            )
                                        })}
                                    </Box>

                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        borderTop: '1px solid #f1f5f9', pt: 1
                                    }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Net</Typography>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: net >= 0 ? '#2e7d32' : '#c62828' }}>
                                            Ksh {numberWithCommas(net)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
            }
        </Box>
    )
}

export default Connect(mapStateToProps)(Invoices)
